import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../layout/Layout';

interface PushTemplate {
  id: number;
  name: string;
  title: string;
  body: string;
  image_url: string | null;
  notification_type: string;
  created_at: string;
}

interface PushHistoryItem {
  id: number;
  title: string;
  body: string;
  image_url: string | null;
  total_recipients: number;
  successful_sends: number;
  failed_sends: number;
  status: string;
  admin_name: string;
  created_at: string;
  started_at: string;
  completed_at: string;
}

interface Schedule {
  id: number;
  name: string;
  schedule_type: string;
  schedule_time: string;
  schedule_days: number[] | null;
  is_active: boolean;
  messages: ScheduleMessage[];
  creator: { name: string };
}

interface ScheduleMessage {
  id: number;
  title: string;
  body: string;
  image_url: string | null;
  priority: number;
}

const SendPush: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'compose' | 'templates' | 'schedules' | 'history'>('compose');
  
  // Template state
  const [templates, setTemplates] = useState<PushTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    body: '',
    image_url: '',
    notification_type: 'general'
  });
  
  // Push compose state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  // Recipients state (counts from get-filtered-users, includes device token breakdown)
  const [recipientCount, setRecipientCount] = useState(0);
  const [recipientWithTokenCount, setRecipientWithTokenCount] = useState(0);
  const [recipientWithoutTokenCount, setRecipientWithoutTokenCount] = useState(0);
  
  // History state
  const [pushHistory, setPushHistory] = useState<PushHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Schedules state
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    schedule_type: 'daily',
    schedule_time: '09:00',
    schedule_days: [] as number[],
    messages: [{ title: '', body: '', image_url: '', priority: 1 }]
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    verification: 'all',
    restriction: 'all',
    maxAge: '',
    relationshipStatus: [] as number[],
    positions: [] as number[],
    bodyTypes: [] as number[],
    tags: [] as number[]
  });

  // User selection state
  const [userSelectionMode, setUserSelectionMode] = useState<'filters' | 'users'>('filters');
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userList, setUserList] = useState<any[]>([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [userListPage, setUserListPage] = useState(1);
  const [userListTotal, setUserListTotal] = useState(0);

  const loadPreSelectedUsers = async (userIds: number[]) => {
    try {
      setUserSelectionMode('users');
      const response = await axios.post('/admin/users', {
        draw: 1,
        start: 0,
        length: userIds.length,
        search: { value: '' },
        filters: {},
        user_ids: userIds
      });
      
      if (response.data && response.data.data) {
        setSelectedUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading pre-selected users:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get('/admin/push-templates');
      if (response.data.success) {
        setTemplates(response.data.data.templates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadRecipientStats = useCallback(async () => {
    try {
      let body: Record<string, unknown>;
      if (userSelectionMode === 'filters') {
        body = { filters };
      } else {
        if (selectedUsers.length === 0) {
          setRecipientCount(0);
          setRecipientWithTokenCount(0);
          setRecipientWithoutTokenCount(0);
          return;
        }
        body = { user_ids: selectedUsers.map(u => u.id) };
      }
      const response = await axios.post('/admin/push-notifications/get-filtered-users', body);
      if (response.data.success) {
        const d = response.data.data;
        setRecipientCount(d.count);
        setRecipientWithTokenCount(d.count_with_device_token ?? 0);
        setRecipientWithoutTokenCount(d.count_without_device_token ?? 0);
      }
    } catch (error) {
      console.error('Error loading recipient stats:', error);
    }
  }, [filters, userSelectionMode, selectedUsers]);

  const loadUserList = useCallback(async () => {
    setUserListLoading(true);
    try {
      const response = await axios.post('/admin/users', {
        draw: userListPage,
        start: (userListPage - 1) * 10,
        length: 10,
        search: { value: userSearch },
        filters: {}
      });
      
      if (response.data) {
        setUserList(response.data.data || []);
        setUserListTotal(response.data.recordsFiltered || 0);
      }
    } catch (error) {
      console.error('Error loading user list:', error);
    } finally {
      setUserListLoading(false);
    }
  }, [userListPage, userSearch]);

  const loadSchedules = useCallback(async () => {
    try {
      const response = await axios.get('/admin/push-schedules');
      if (response.data.success) {
        setSchedules(response.data.data.schedules);
      }
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  }, []);

  const loadPushHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`/admin/push-notifications/history?page=${historyPage}&limit=10`);
      if (response.data.success) {
        setPushHistory(response.data.data.history);
        setHistoryTotal(response.data.data.pagination.total);
      }
    } catch (error) {
      console.error('Error loading push history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadPushHistory();
    }
  }, [activeTab, loadPushHistory]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (activeTab === 'compose') {
        if (userSelectionMode === 'filters') {
          loadRecipientStats();
        } else {
          loadUserList();
          loadRecipientStats();
        }
      }
    }, 500);
    return () => clearTimeout(debounce);
  }, [activeTab, loadRecipientStats, loadUserList, userSelectionMode]);

  useEffect(() => {
    loadTemplates();
    loadSchedules();

    if (location.state?.userIds && location.state.userIds.length > 0) {
      loadPreSelectedUsers(location.state.userIds);
    }
  }, [location.state, loadSchedules]);

  const handleTemplateSelect = (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplateId(templateId);
      setTitle(template.title);
      setBody(template.body);
      setImageUrl(template.image_url || '');
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplateId) {
        const response = await axios.put(`/admin/push-templates/${editingTemplateId}`, templateForm);
        if (response.data.success) {
          alert('Template updated successfully');
          loadTemplates();
          setShowTemplateModal(false);
          setEditingTemplateId(null);
          setTemplateForm({ name: '', title: '', body: '', image_url: '', notification_type: 'general' });
        }
      } else {
        const response = await axios.post('/admin/push-templates', templateForm);
        if (response.data.success) {
          alert('Template saved successfully');
          loadTemplates();
          setShowTemplateModal(false);
          setTemplateForm({ name: '', title: '', body: '', image_url: '', notification_type: 'general' });
        }
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save template');
    }
  };

  const handleEditTemplate = (template: PushTemplate) => {
    setEditingTemplateId(template.id);
    setTemplateForm({
      name: template.name,
      title: template.title,
      body: template.body,
      image_url: template.image_url || '',
      notification_type: template.notification_type
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await axios.delete(`/admin/push-templates/${id}`);
      loadTemplates();
      if (selectedTemplateId === id) {
        setSelectedTemplateId(null);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete template');
    }
  };

  const handleSendTest = async () => {
    if (!title || !body) {
      alert('Please enter title and body');
      return;
    }

    try {
      const response = await axios.post('/admin/push-notifications/send-test', {
        title,
        body,
        image_url: imageUrl || null
      });

      if (response.data.success) {
        alert('Test push sent successfully!');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send test push');
    }
  };

  const handleSendBulk = async () => {
    if (!title || !body) {
      alert('Please enter title and body');
      return;
    }

    const recipientCount = userSelectionMode === 'users' ? selectedUsers.length : getRecipientCount();
    
    if (recipientCount === 0) {
      alert('No recipients selected');
      return;
    }

    if (
      !window.confirm(
        `Send push to ${recipientCount} users?\n\n` +
          `With a registered device token: ${recipientWithTokenCount}\n` +
          `Without a device token (may fail or skip): ${recipientWithoutTokenCount}`
      )
    ) {
      return;
    }

    setIsSending(true);

    try {
      const payload: any = {
        title,
        body,
        image_url: imageUrl || null,
        template_id: selectedTemplateId
      };

      if (userSelectionMode === 'users') {
        payload.user_ids = selectedUsers.map(u => u.id);
      } else {
        payload.filters = filters;
      }

      const response = await axios.post('/admin/push-notifications/send', payload);

      if (response.data.success) {
        alert(`Push notification queued for ${recipientCount} users!`);
        setTitle('');
        setBody('');
        setImageUrl('');
        setSelectedTemplateId(null);
        setSelectedUsers([]);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send push notification');
    } finally {
      setIsSending(false);
    }
  };

  const handleUserSelect = (user: any) => {
    if (selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleRemoveSelectedUser = (userId: number) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const getRecipientCount = () => {
    if (userSelectionMode === 'filters') {
      return recipientCount;
    } else {
      return selectedUsers.length;
    }
  };

  const handleSaveSchedule = async () => {
    try {
      const response = await axios.post('/admin/push-schedules', {
        ...scheduleForm,
        target_filters: filters
      });
      
      if (response.data.success) {
        alert('Schedule created successfully');
        loadSchedules();
        setShowScheduleModal(false);
        setScheduleForm({
          name: '',
          schedule_type: 'daily',
          schedule_time: '09:00',
          schedule_days: [],
          messages: [{ title: '', body: '', image_url: '', priority: 1 }]
        });
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create schedule');
    }
  };

  const handleToggleSchedule = async (id: number) => {
    try {
      await axios.patch(`/admin/push-schedules/${id}/toggle`);
      loadSchedules();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle schedule');
    }
  };

  const handleDeleteSchedule = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await axios.delete(`/admin/push-schedules/${id}`);
      loadSchedules();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete schedule');
    }
  };

  return (
    <Layout>
      <style>{`
        .template-var {
          cursor: pointer;
          padding: 2px 6px;
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          transition: all 0.2s ease;
          font-size: 12px;
          display: inline-block;
          margin: 2px;
        }
        .template-var:hover {
          background-color: #e9ecef;
          border-color: #006175;
          color: #006175;
        }
        .notification-preview {
          max-width: 400px;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 12px;
          background: white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
      `}</style>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Send Push Notification</h2>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'compose' ? 'active' : ''}`}
              onClick={() => setActiveTab('compose')}
            >
              Compose
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`}
              onClick={() => setActiveTab('templates')}
            >
              Templates
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'schedules' ? 'active' : ''}`}
              onClick={() => setActiveTab('schedules')}
            >
              Schedules
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
          </li>
        </ul>

        {/* Compose Tab */}
        {activeTab === 'compose' && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-4">Compose Push Notification</h5>

                  {/* User Selection Mode */}
                  <div className="mb-3">
                    <label className="form-label">Select Recipients</label>
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn ${userSelectionMode === 'filters' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setUserSelectionMode('filters')}
                      >
                        By Filters
                      </button>
                      <button
                        type="button"
                        className={`btn ${userSelectionMode === 'users' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setUserSelectionMode('users')}
                      >
                        Select Users
                      </button>
                    </div>
                  </div>

                  {/* Filters Mode */}
                  {userSelectionMode === 'filters' && (
                    <div className="card mb-3 bg-light">
                      <div className="card-body">
                        <h6>Filter Options</h6>
                        <div className="row g-2">
                          <div className="col-md-4">
                            <label className="form-label small">Verification</label>
                            <select
                              className="form-select form-select-sm"
                              value={filters.verification}
                              onChange={(e) => setFilters({ ...filters, verification: e.target.value })}
                            >
                              <option value="all">All</option>
                              <option value="verified">Verified</option>
                              <option value="not_verified">Not Verified</option>
                            </select>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small">Status</label>
                            <select
                              className="form-select form-select-sm"
                              value={filters.restriction}
                              onChange={(e) => setFilters({ ...filters, restriction: e.target.value })}
                            >
                              <option value="all">All</option>
                              <option value="not_banned">Active</option>
                              <option value="banned">Banned</option>
                            </select>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small">Max Age</label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={filters.maxAge}
                              onChange={(e) => setFilters({ ...filters, maxAge: e.target.value })}
                              placeholder="e.g., 50"
                            />
                          </div>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted d-block">
                            Matching users: <strong>{recipientCount}</strong>
                          </small>
                          <small className="text-muted d-block">
                            With device token: <strong className="text-success">{recipientWithTokenCount}</strong>
                            {' · '}
                            Without token: <strong className="text-warning">{recipientWithoutTokenCount}</strong>
                          </small>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* User Selection Mode */}
                  {userSelectionMode === 'users' && (
                    <div className="card mb-3 bg-light">
                      <div className="card-body">
                        <h6>Select Users</h6>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-2"
                          placeholder="Search users..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                        />
                        
                        {selectedUsers.length > 0 && (
                          <div className="mb-2">
                            <small className="text-muted d-block">Selected: {selectedUsers.length}</small>
                            <small className="text-muted d-block">
                              With device token: <strong className="text-success">{recipientWithTokenCount}</strong>
                              {' · '}
                              Without token: <strong className="text-warning">{recipientWithoutTokenCount}</strong>
                            </small>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {selectedUsers.slice(0, 5).map(user => (
                                <span key={user.id} className="badge bg-primary">
                                  {user.name}
                                  <i 
                                    className="bx bx-x ms-1"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleRemoveSelectedUser(user.id)}
                                  ></i>
                                </span>
                              ))}
                              {selectedUsers.length > 5 && (
                                <span className="badge bg-secondary">+{selectedUsers.length - 5} more</span>
                              )}
                            </div>
                          </div>
                        )}

                        {userListLoading ? (
                          <div className="text-center py-2">
                            <div className="spinner-border spinner-border-sm" role="status"></div>
                          </div>
                        ) : (
                          <>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              {userList.map(user => (
                                <div key={user.id} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={selectedUsers.some(u => u.id === user.id)}
                                    onChange={() => handleUserSelect(user)}
                                  />
                                  <label className="form-check-label small">
                                    {user.name} ({user.email})
                                  </label>
                                </div>
                              ))}
                            </div>
                            
                            {/* Pagination for user list */}
                            {userListTotal > 10 && (
                              <div className="d-flex justify-content-center align-items-center mt-2 gap-2">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => setUserListPage(userListPage - 1)}
                                  disabled={userListPage === 1}
                                >
                                  <i className="bx bx-chevron-left"></i>
                                </button>
                                <span className="small text-muted">
                                  Page {userListPage} of {Math.ceil(userListTotal / 10)}
                                </span>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => setUserListPage(userListPage + 1)}
                                  disabled={userListPage >= Math.ceil(userListTotal / 10)}
                                >
                                  <i className="bx bx-chevron-right"></i>
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Template Selector */}
                  <div className="mb-3">
                    <label className="form-label">Use Template (Optional)</label>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select"
                        value={selectedTemplateId || ''}
                        onChange={(e) => handleTemplateSelect(parseInt(e.target.value))}
                      >
                        <option value="">Select a template...</option>
                        {templates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => setShowTemplateModal(true)}
                      >
                        <i className="bx bx-plus-circle"></i> New
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="mb-3">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter notification title"
                      maxLength={50}
                    />
                    <small className="text-muted">{title.length}/50 characters</small>
                  </div>

                  {/* Body */}
                  <div className="mb-3">
                    <label className="form-label">Body *</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Enter notification body"
                      maxLength={200}
                    />
                    <small className="text-muted">{body.length}/200 characters</small>
                  </div>

                  {/* Image URL */}
                  <div className="mb-3">
                    <label className="form-label">Image URL (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  {/* Template Variables */}
                  <div className="mb-3">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="bx bx-code-alt me-2"></i>
                          Available Template Variables (click to copy)
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <h6 className="text-primary small">Basic Info</h6>
                            <div className="d-flex flex-wrap">
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{name}}')}>{'{{name}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{email}}')}>{'{{email}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{age}}')}>{'{{age}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{location}}')}>{'{{location}}'}</code>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-primary small">Profile Info</h6>
                            <div className="d-flex flex-wrap">
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{position}}')}>{'{{position}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{body_type}}')}>{'{{body_type}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{relationship_status}}')}>{'{{relationship_status}}'}</code>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      className="btn btn-outline-primary"
                      onClick={handleSendTest}
                      disabled={isSending || !title || !body}
                    >
                      <i className="bx bx-send me-2"></i>
                      Send Test
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSendBulk}
                      disabled={isSending || !title || !body || getRecipientCount() === 0}
                    >
                      {isSending ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-send me-2"></i>
                          Send to {getRecipientCount()} Users
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-3">Preview</h5>
                  <div className="notification-preview">
                    <div className="d-flex align-items-start">
                      <div className="flex-shrink-0">
                        <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" 
                             style={{ width: '40px', height: '40px' }}>
                          <i className="bx bx-bell text-white"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h6 className="mb-1">{title || 'Notification Title'}</h6>
                        <p className="text-muted small mb-2">{body || 'Notification body will appear here...'}</p>
                        {imageUrl && (
                          <img src={imageUrl} alt="Preview" className="img-fluid rounded" 
                               style={{ maxHeight: '150px', objectFit: 'cover' }} 
                               onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Push Templates</h5>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowTemplateModal(true)}
                >
                  <i className="bx bx-plus-circle me-2"></i>
                  New Template
                </button>
              </div>

              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map(template => (
                      <tr key={template.id}>
                        <td>{template.name}</td>
                        <td>
                          <span className={`badge bg-${
                            template.notification_type === 'promotional' ? 'success' :
                            template.notification_type === 'warning' ? 'warning' :
                            template.notification_type === 'info' ? 'info' : 'secondary'
                          }`}>
                            {template.notification_type}
                          </span>
                        </td>
                        <td>{template.title}</td>
                        <td>{new Date(template.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditTemplate(template)}
                              title="Edit Template"
                            >
                              <i className="bx bx-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteTemplate(template.id)}
                              title="Delete Template"
                            >
                              <i className="bx bx-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Schedules Tab */}
        {activeTab === 'schedules' && (
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Push Schedules</h5>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowScheduleModal(true)}
                >
                  <i className="bx bx-plus-circle me-2"></i>
                  New Schedule
                </button>
              </div>

              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Time</th>
                      <th>Messages</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map(schedule => (
                      <tr key={schedule.id}>
                        <td>{schedule.name}</td>
                        <td>
                          <span className="badge bg-info">{schedule.schedule_type}</span>
                        </td>
                        <td>{schedule.schedule_time}</td>
                        <td>{schedule.messages?.length || 0} variants</td>
                        <td>
                          <span className={`badge bg-${schedule.is_active ? 'success' : 'secondary'}`}>
                            {schedule.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleToggleSchedule(schedule.id)}
                              title={schedule.is_active ? 'Deactivate' : 'Activate'}
                            >
                              <i className={`bx bx-${schedule.is_active ? 'pause' : 'play'}`}></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteSchedule(schedule.id)}
                              title="Delete Schedule"
                            >
                              <i className="bx bx-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="card">
            <div className="card-body">
              <h5 className="mb-3">Push Notification History</h5>

              {historyLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status"></div>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Title</th>
                          <th>Recipients</th>
                          <th>Success</th>
                          <th>Failed</th>
                          <th>Status</th>
                          <th>Admin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pushHistory.map(push => (
                          <tr key={push.id}>
                            <td>{new Date(push.created_at).toLocaleString()}</td>
                            <td>{push.title}</td>
                            <td>{push.total_recipients}</td>
                            <td>
                              <span className="badge bg-success">{push.successful_sends}</span>
                            </td>
                            <td>
                              <span className="badge bg-danger">{push.failed_sends}</span>
                            </td>
                            <td>
                              <span className={`badge bg-${
                                push.status === 'completed' ? 'success' :
                                push.status === 'in_progress' ? 'warning' :
                                push.status === 'failed' ? 'danger' : 'secondary'
                              }`}>
                                {push.status}
                              </span>
                            </td>
                            <td>{push.admin_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {historyTotal > 10 && (
                    <nav>
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${historyPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setHistoryPage(historyPage - 1)}
                          >
                            Previous
                          </button>
                        </li>
                        <li className="page-item active">
                          <span className="page-link">{historyPage}</span>
                        </li>
                        <li className={`page-item ${historyPage * 10 >= historyTotal ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setHistoryPage(historyPage + 1)}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Template Modal */}
        {showTemplateModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingTemplateId ? 'Edit' : 'New'} Push Template</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowTemplateModal(false);
                      setEditingTemplateId(null);
                      setTemplateForm({ name: '', title: '', body: '', image_url: '', notification_type: 'general' });
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Template Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Template Type</label>
                    <select
                      className="form-select"
                      value={templateForm.notification_type}
                      onChange={(e) => setTemplateForm({ ...templateForm, notification_type: e.target.value })}
                    >
                      <option value="general">General</option>
                      <option value="promotional">Promotional</option>
                      <option value="warning">Warning</option>
                      <option value="info">Info</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={templateForm.title}
                      onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Body *</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={templateForm.body}
                      onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Image URL</label>
                    <input
                      type="text"
                      className="form-control"
                      value={templateForm.image_url}
                      onChange={(e) => setTemplateForm({ ...templateForm, image_url: e.target.value })}
                    />
                  </div>

                  {/* Template Variables in Modal */}
                  <div className="mb-3">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="bx bx-code-alt me-2"></i>
                          Available Template Variables (click to copy)
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <h6 className="text-primary small">Basic Info</h6>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{name}}')}>{'{{name}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{email}}')}>{'{{email}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{age}}')}>{'{{age}}'}</code>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-primary small">Profile Info</h6>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{position}}')}>{'{{position}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{body_type}}')}>{'{{body_type}}'}</code>
                              <code className="template-var" onClick={() => navigator.clipboard.writeText('{{location}}')}>{'{{location}}'}</code>
                            </div>
                          </div>
                        </div>
                        <small className="text-muted">Click any variable to copy it, then paste in title or body.</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowTemplateModal(false);
                      setEditingTemplateId(null);
                      setTemplateForm({ name: '', title: '', body: '', image_url: '', notification_type: 'general' });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveTemplate}
                    disabled={!templateForm.name || !templateForm.title || !templateForm.body}
                  >
                    {editingTemplateId ? 'Update Template' : 'Save Template'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Modal */}
        {showScheduleModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">New Push Schedule</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowScheduleModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Schedule Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={scheduleForm.name}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Schedule Type</label>
                      <select
                        className="form-select"
                        value={scheduleForm.schedule_type}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_type: e.target.value })}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={scheduleForm.schedule_time}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_time: e.target.value })}
                      />
                    </div>
                  </div>

                  {scheduleForm.schedule_type === 'weekly' && (
                    <div className="mb-3">
                      <label className="form-label">Days</label>
                      <div className="d-flex gap-2">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`btn btn-sm ${scheduleForm.schedule_days.includes(idx) ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => {
                              const days = scheduleForm.schedule_days.includes(idx)
                                ? scheduleForm.schedule_days.filter(d => d !== idx)
                                : [...scheduleForm.schedule_days, idx];
                              setScheduleForm({ ...scheduleForm, schedule_days: days });
                            }}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Message Variants</label>
                    {scheduleForm.messages.map((msg, idx) => (
                      <div key={idx} className="card mb-2">
                        <div className="card-body">
                          <div className="mb-2">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Title"
                              value={msg.title}
                              onChange={(e) => {
                                const messages = [...scheduleForm.messages];
                                messages[idx].title = e.target.value;
                                setScheduleForm({ ...scheduleForm, messages });
                              }}
                            />
                          </div>
                          <div className="mb-2">
                            <textarea
                              className="form-control form-control-sm"
                              rows={2}
                              placeholder="Body"
                              value={msg.body}
                              onChange={(e) => {
                                const messages = [...scheduleForm.messages];
                                messages[idx].body = e.target.value;
                                setScheduleForm({ ...scheduleForm, messages });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setScheduleForm({
                          ...scheduleForm,
                          messages: [...scheduleForm.messages, { title: '', body: '', image_url: '', priority: 1 }]
                        });
                      }}
                    >
                      <i className="bx bx-plus"></i> Add Variant
                    </button>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowScheduleModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveSchedule}
                    disabled={!scheduleForm.name || scheduleForm.messages.length === 0}
                  >
                    Create Schedule
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SendPush;

